import { useCallback, useRef, useState } from 'react';

import { Loader2, Send, X } from 'lucide-react';

import { Button } from '@/components/ui/button';
import { streamTemplateEdit } from '@/lib/api/template-ai.api';

import type { TemplateAiEditRequest, TemplateAiSSEEvent } from '@/lib/api/template-ai.api';

const MODEL_OPTIONS = [
  { value: 'gemini-2.5-flash', label: 'Gemini 2.5 Flash' },
  { value: 'gemini-2.5-pro', label: 'Gemini 2.5 Pro' },
  { value: 'gemini-2.0-flash', label: 'Gemini 2.0 Flash' },
  { value: 'gemini-2.0-flash-lite', label: 'Gemini 2.0 Flash Lite' },
] as const;

interface ChatMessage {
  role: 'user' | 'model';
  content: string;
}

interface AiAssistantPanelProps {
  currentContent: string;
  availableVariables: Array<{ path: string; description: string }>;
  editorContext: 'prompt' | 'workflow_email' | 'workflow_sms' | 'workflow_json';
  onProposedEdit: (proposedContent: string) => void;
  onClose: () => void;
}

export const AiAssistantPanel = ({
  currentContent,
  availableVariables,
  editorContext,
  onProposedEdit,
  onClose,
}: AiAssistantPanelProps) => {
  const [messages, setMessages] = useState<ChatMessage[]>([]);
  const [input, setInput] = useState('');
  const [isStreaming, setIsStreaming] = useState(false);
  const [streamingText, setStreamingText] = useState('');
  const [selectedModel, setSelectedModel] = useState('gemini-2.5-flash');
  const [error, setError] = useState<string | null>(null);
  const abortRef = useRef<AbortController | null>(null);
  const messagesEndRef = useRef<HTMLDivElement>(null);

  const scrollToBottom = useCallback(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, []);

  const handleSend = useCallback(async () => {
    const instruction = input.trim();
    if (!instruction || isStreaming) return;

    setInput('');
    setError(null);

    const userMessage: ChatMessage = { role: 'user', content: instruction };
    setMessages((prev) => [...prev, userMessage]);
    setIsStreaming(true);
    setStreamingText('');

    const abortController = new AbortController();
    abortRef.current = abortController;

    const conversationHistory = messages.map((m) => ({ role: m.role, content: m.content }));

    const dto: TemplateAiEditRequest = {
      instruction,
      currentContent,
      conversationHistory,
      availableVariables,
      editorContext,
      model: selectedModel,
    };

    let resultContent = '';

    try {
      await streamTemplateEdit(
        dto,
        (event: TemplateAiSSEEvent) => {
          if (event.type === 'thinking') {
            setStreamingText('Thinking...');
          } else if (event.type === 'chunk') {
            setStreamingText((prev) =>
              prev === 'Thinking...' ? event.content : prev + event.content,
            );
          } else if (event.type === 'result') {
            resultContent = event.content;
            setMessages((prev) => [
              ...prev,
              { role: 'model', content: 'Edit proposed — review the diff above.' },
            ]);
            onProposedEdit(event.content);
          } else if (event.type === 'error') {
            setError(event.content);
          }
          scrollToBottom();
        },
        abortController.signal,
      );
    } catch (err) {
      if (err instanceof Error && err.name !== 'AbortError') {
        setError(err.message);
      }
    } finally {
      setIsStreaming(false);
      setStreamingText('');
      abortRef.current = null;
      if (!resultContent) {
        scrollToBottom();
      }
    }
  }, [
    input,
    isStreaming,
    messages,
    currentContent,
    availableVariables,
    editorContext,
    selectedModel,
    onProposedEdit,
    scrollToBottom,
  ]);

  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent) => {
      if (e.key === 'Enter' && !e.shiftKey) {
        e.preventDefault();
        handleSend();
      }
    },
    [handleSend],
  );

  const handleAbort = useCallback(() => {
    abortRef.current?.abort();
  }, []);

  return (
    <div className="absolute top-0 right-0 bottom-0 z-[10] flex w-80 flex-col border-l border-[#313244] bg-[#1e1e2e] shadow-2xl">
      <div className="flex items-center justify-between border-b border-[#313244] px-3 py-1.5">
        <div className="flex items-center gap-2">
          <select
            className="rounded border-none bg-[#313244] px-2 py-0.5 text-xs text-[#cdd6f4] outline-none"
            value={selectedModel}
            onChange={(e) => setSelectedModel(e.target.value)}
            disabled={isStreaming}
          >
            {MODEL_OPTIONS.map((m) => (
              <option key={m.value} value={m.value}>
                {m.label}
              </option>
            ))}
          </select>
          <span className="text-xs text-[#6c7086]">AI Template Assistant</span>
        </div>
        <Button
          variant="ghost"
          size="icon"
          className="h-6 w-6 text-[#6c7086] hover:text-[#cdd6f4]"
          onClick={onClose}
        >
          <X className="h-3.5 w-3.5" />
        </Button>
      </div>

      <div className="flex-1 space-y-2 overflow-y-auto px-3 py-2 text-xs">
        {messages.length === 0 && !isStreaming && (
          <p className="text-[#6c7086] italic">
            Describe what you want to change in the template...
          </p>
        )}
        {messages.map((msg, i) => (
          <div
            key={i}
            className={`rounded px-2 py-1 ${
              msg.role === 'user'
                ? 'ml-8 bg-[#45475a] text-[#cdd6f4]'
                : 'mr-8 bg-[#313244] text-[#a6adc8]'
            }`}
          >
            {msg.content}
          </div>
        ))}
        {isStreaming && streamingText && (
          <div className="mr-8 flex items-center gap-1.5 rounded bg-[#313244] px-2 py-1 text-[#a6adc8]">
            <Loader2 className="h-3 w-3 shrink-0 animate-spin" />
            <span className="truncate">
              {streamingText === 'Thinking...' ? streamingText : 'Generating edit...'}
            </span>
          </div>
        )}
        {error && (
          <div className="rounded bg-red-900/30 px-2 py-1 text-xs text-red-300">{error}</div>
        )}
        <div ref={messagesEndRef} />
      </div>

      <div className="flex gap-2 border-t border-[#313244] px-3 py-2">
        <input
          className="flex-1 rounded border-none bg-[#313244] px-3 py-1.5 text-xs text-[#cdd6f4] placeholder-[#6c7086] outline-none focus:ring-1 focus:ring-[#89b4fa]"
          placeholder="e.g. Add a rule to never ask for name if already known..."
          value={input}
          onChange={(e) => setInput(e.target.value)}
          onKeyDown={handleKeyDown}
          disabled={isStreaming}
        />
        {isStreaming ? (
          <Button
            size="sm"
            variant="ghost"
            className="h-7 px-2 text-red-400 hover:text-red-300"
            onClick={handleAbort}
          >
            Stop
          </Button>
        ) : (
          <Button
            size="sm"
            variant="ghost"
            className="h-7 px-2 text-[#89b4fa] hover:text-[#b4befe]"
            onClick={handleSend}
            disabled={!input.trim()}
          >
            <Send className="h-3.5 w-3.5" />
          </Button>
        )}
      </div>
    </div>
  );
};
